import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { useSeo } from '@/hooks/useSeo';
import { useAccessoriGroups } from '@/hooks/useAccessoriGroups';
import { whatsappLink } from '@/lib/whatsapp';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const ALL = 'Tutti';

export default function AccessoriPage() {
  useSeo({
    title: 'Accessori — Portachiavi e idee regalo | Ciro Gallotti',
    description:
      'Gli Accessori di Ciro Gallotti: portachiavi e piccoli oggetti preziosi realizzati con cura artigianale a Napoli. Prezzo su richiesta via WhatsApp.',
    path: '/accessori',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://cirogallotti.com/' },
        { '@type': 'ListItem', position: 2, name: 'Accessori', item: 'https://cirogallotti.com/accessori' },
      ],
    },
  });

  const groups = useAccessoriGroups();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const subParam = searchParams.get('sub');
  const tabs = [ALL, ...groups];
  const active = tabs.includes(subParam) ? subParam : ALL;

  useEffect(() => {
    axios
      .get(`${API}/products?collection=Accessori`)
      .then((res) => setProducts(res.data || []))
      .catch((e) => console.error('Errore caricamento accessori:', e))
      .finally(() => setLoading(false));
  }, []);

  const setSub = (sub) => {
    const next = new URLSearchParams(searchParams);
    if (sub === ALL) next.delete('sub'); else next.set('sub', sub);
    setSearchParams(next);
  };

  const count = (sub) => (sub === ALL ? products.length : products.filter((p) => p.category === sub).length);
  const visible = products
    .filter((p) => active === ALL || p.category === active)
    .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return (
    <div className="bg-ivory pt-20 md:pt-22" data-testid="accessori-page">
      <section className="bg-secondary/40" data-testid="accessori-hero">
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-px bg-bordeaux" />
            <p className="text-eyebrow text-bordeaux">I Dettagli</p>
          </div>
          <h1 className="font-display text-h1 text-ink">Accessori</h1>
          <p className="mt-6 max-w-2xl text-ink/70 leading-relaxed">
            Portachiavi e piccoli oggetti preziosi, pensati per accompagnarti ogni giorno o per un regalo
            che resta. Per ogni pezzo ti diamo il prezzo su richiesta, direttamente su WhatsApp.
          </p>
        </div>
      </section>

      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <div className="flex flex-wrap items-center gap-3 mb-10" data-testid="accessori-tabs">
          {tabs.map((sub) => (
            <button
              key={sub}
              onClick={() => setSub(sub)}
              className={`px-6 py-2.5 rounded-full text-eyebrow border transition-colors duration-hover ${
                active === sub ? 'border-bordeaux bg-bordeaux text-ivory' : 'border-line text-ink/70 hover:border-ink'
              }`}
              data-testid={`accessori-tab-${sub}`}
            >
              {sub}
              <span className={`ml-2 ${active === sub ? 'text-ivory/70' : 'text-muted'}`}>{count(sub)}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-line rounded-md mb-5" />
                <div className="h-5 bg-line w-40 mb-2" />
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-24 border border-line rounded-2xl" data-testid="accessori-empty">
            <p className="font-display text-h3 text-ink mb-3">Presto disponibili</p>
            <p className="text-muted mb-8 max-w-md mx-auto">
              Stiamo preparando i nuovi accessori. Scrivici su WhatsApp per sapere cosa abbiamo in negozio.
            </p>
            <a href={whatsappLink('Ciao, vorrei informazioni sugli accessori disponibili.')} target="_blank" rel="noopener noreferrer">
              <Button variant="accent" data-testid="accessori-empty-whatsapp">Scrivici su WhatsApp</Button>
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-12">
            {visible.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
